import React, { useCallback, useState, useRef } from 'react'
import CommonButtons from 'src/shared/components/CommonButtons'
import useSummeryReport from './hooks/useSummeryReport'
import { useUser } from 'src/shared/context/UserContext'
import { DailySummeryPrint } from 'src/print-configuration/printBaseCode'

function GenerateDailySummery({ userId }: { userId?: string }) {
  const { createReport: fetchSummery, loading, summery } = useSummeryReport()
  const [showModal, setShowModal] = useState(false)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [dateError, setDateError] = useState('')
  const { user } = useUser()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  const copies = 1

  const today = new Date().toISOString().split('T')[0]

  const formatNumber = (val: any) =>
    (val ?? 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

  const formatDate = (dateStr: any) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  }

  // ---- Date change handlers with validation ----
  const handleFromDateChange = (value: string) => {
    setFromDate(value)
    setDateError('')
    if (toDate && value && toDate < value) {
      setToDate('')
    }
  }

  const handleToDateChange = (value: string) => {
    if (fromDate && value < fromDate) {
      setDateError('To Date cannot be before From Date')
      return
    }
    if (value > today) {
      setDateError('To Date cannot be in the future')
      return
    }
    setDateError('')
    setToDate(value)
  }

  // report is now the `report_summary` object
  const buildContent = (report: any) => `
    <div class="receipt">
      <div class="center bold text-lg">${user?.orgName}</div>
      <div class="center">${user?.firstName} ${user?.middleName} ${user?.lastName}</div>
      <div class="center bold">DAY SUMMARY REPORT</div>
      <div class="center">${report?.period_label ?? ''}</div>
      <div class="center">FROM DATE: ${formatDate(report?.start_date)}</div>
      <div class="center">TO DATE: ${formatDate(report?.end_date)}</div>

      <div class="dashed-line"></div>

      <div class="row bold">
        <span>HEAD</span>
        <span>TOTAL</span>
      </div>

      <div class="section-title">ORDER SUMMARY</div>
      <div class="row">
        <span>NUMBER OF ORDERS</span>
        <span>${formatNumber(report?.today_order_summary?.number_of_orders)}</span>
      </div>
      <div class="row">
        <span>TOTAL SALES</span>
        <span>${formatNumber(report?.today_order_summary?.total_sales)}</span>
      </div>
      <div class="row">
        <span>NET SALES</span>
        <span>${formatNumber(report?.today_order_summary?.net_sales)}</span>
      </div>
      <div class="row">
        <span>ORDERS RECEIVED</span>
        <span>${formatNumber(report?.today_order_summary?.orders_received)}</span>
      </div>
      <div class="row">
        <span>RECEIVED BY CASH</span>
        <span>${formatNumber(report?.today_order_summary?.received_by_cash)}</span>
      </div>
      <div class="row">
        <span>RECEIVED BY CARD</span>
        <span>${formatNumber(report?.today_order_summary?.received_by_card)}</span>
      </div>

      <div class="section-title">CANCELLED ORDERS</div>
      <div class="row">
        <span>REFUND BY CASH</span>
        <span>${formatNumber(report?.cancelled_orders_summary?.refund_by_cash)}</span>
      </div>
      <div class="row">
        <span>REFUND BY CARD</span>
        <span>${formatNumber(report?.cancelled_orders_summary?.refund_by_card)}</span>
      </div>

      <div class="section-title">PREVIOUS OUTSTANDING RECEIVED</div>
      <div class="row">
        <span>RECEIVED BY CASH</span>
        <span>${formatNumber(report?.previous_outstanding_received?.received_by_cash)}</span>
      </div>
      <div class="row">
        <span>RECEIVED BY CARD</span>
        <span>${formatNumber(report?.previous_outstanding_received?.received_by_card)}</span>
      </div>
      <div class="row">
        <span>OUTSTANDING RECEIVED</span>
        <span>${formatNumber(report?.previous_outstanding_received?.outstanding_received)}</span>
      </div>

      <div class="section-title">TODAY OVERALL RECEIVED</div>
      <div class="row">
        <span>RECEIVED BY CASH</span>
        <span>${formatNumber(report?.today_overall_received?.received_by_cash)}</span>
      </div>
      <div class="row">
        <span>RECEIVED BY CARD</span>
        <span>${formatNumber(report?.today_overall_received?.received_by_card)}</span>
      </div>
      <div class="row">
        <span>REFUND AMOUNT</span>
        <span>${formatNumber(report?.today_overall_received?.refund_amount)}</span>
      </div>
      <div class="row bold">
        <span>TOTAL RECEIVED</span>
        <span>${formatNumber(report?.today_overall_received?.total_received)}</span>
      </div>

      <div class="dashed-line"></div>
      <div class="center small">Printed on ${new Date().toLocaleString()}</div>
    </div>
  `

  const buildPrintData = (report: any) => `
    ${DailySummeryPrint}
    <body>
      ${Array.from({ length: copies })
      .map(
        (_, index) => `
          <div class="page ${index < copies - 1 ? "copy-break" : ""}">
            ${buildContent(report)}
          </div>
        `,
      )
      .join("")}
    </body>
    </html>
  `

  const printInSameTab = (html: string) => {
    if (iframeRef.current) {
      document.body.removeChild(iframeRef.current)
      iframeRef.current = null
    }

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    document.body.appendChild(iframe)
    iframeRef.current = iframe

    const doc = iframe.contentWindow?.document
    if (!doc) return

    doc.open()
    doc.write(html)
    doc.close()

    iframe.onload = () => {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
    }

    if (iframe.contentWindow) {
      iframe.contentWindow.onafterprint = () => {
        if (iframeRef.current) {
          document.body.removeChild(iframeRef.current)
          iframeRef.current = null
        }
      }
    }

    setTimeout(() => {
      if (iframeRef.current) {
        document.body.removeChild(iframeRef.current)
        iframeRef.current = null
      }
    }, 8000)
  }

  const createReport = useCallback(async () => {
    if (!fromDate || !toDate) {
      setDateError('Please select both From and To dates')
      return
    }
    if (toDate < fromDate) {
      setDateError('To Date cannot be before From Date')
      return
    }
    if (toDate > today) {
      setDateError('To Date cannot be in the future')
      return
    }

    let userIdToUse = userId
    if (!userIdToUse || userIdToUse === 'undefined') {
      userIdToUse = "" // Default to empty string if userId is null or undefined
    }

    const result = await fetchSummery(fromDate, toDate, userIdToUse)
    setShowModal(false)
    setFromDate("")
    setToDate("")

    // New shape: data lives under `report_summary`, not `report`
    const report = result?.report_summary ?? summery?.report_summary

    if (!report) {
      console.error('No report data available to print')
      return
    }

    const html = buildPrintData(report)

    printInSameTab(html)
  }, [fromDate, toDate, summery])

  return (
    <>
      <CommonButtons
        label={"🖨️ Daily Summery"}
        onClick={() => setShowModal(true)}
        disabled={loading}
        className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-60"
      />
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '10px',
              padding: '20px',
              width: '320px',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Select Report Range</h3>

            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              max={today}
              onChange={(e) => handleFromDateChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
              }}
            />

            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              max={today}
              onChange={(e) => handleToDateChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '8px',
                border: '1px solid #ccc',
                borderRadius: '6px',
              }}
            />

            {dateError && (
              <p style={{ color: '#dc2626', fontSize: '12px', marginTop: 0, marginBottom: '12px' }}>
                {dateError}
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={() => {
                  setShowModal(false)
                  setDateError('')
                }}
                className="rounded-lg bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={createReport}
                disabled={loading}
                className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-60"
              >
                {loading ? 'Loading...' : 'Print'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default GenerateDailySummery